import { useCallback, useEffect, useId, useRef, useState } from "react";
import { api, ApiError } from "../api";
import type { AdminWalletTransfer, WalletDispatchEvidence, WalletErrorEvidence } from "../wallet-types";
import { Button, EmptyState, Modal, Spinner, StatusBadge } from "./ui";

import { amountCents, isDurableWalletReview, needsWalletReview, queryableTransfer, walletSettlementSummary, walletTransferLabel } from "../mobile/wallet-intent";

const dispatchNames: Record<WalletDispatchEvidence["phase"], string> = { started: "首次调用已登记，发送结果未确认", acknowledged: "已收到首次响应，仍以原单结果为准", not_sent: "请求未发出", rejected: "首次请求被拒绝", unknown: "首次请求结果未知" };
const errorNames: Record<WalletErrorEvidence["reason"], string> = { validation: "参数校验", configuration: "接口配置", provider: "供应商返回", transport: "网络传输", protocol: "回执格式或身份", unknown: "未知原因" };
const resolutionReasonNames: Record<NonNullable<AdminWalletTransfer["resolution"]>["reason"], string> = {
  upstream_success: "上游确认成功", upstream_failure: "上游确认失败", manual_confirmed_success: "人工确认成功", manual_confirmed_failure: "人工确认失败",
  not_sent: "请求未发出", provider_rejected: "供应商明确拒绝", provider_mismatch: "场馆不匹配", direction_mismatch: "转账方向不匹配",
  order_mismatch: "订单号不匹配", invalid_amount: "金额异常", amount_exceeds_request: "实际金额超过申请", zero_success_amount: "成功金额为零",
  integration_scope_mismatch: "商户范围不一致", integration_scope_unverified: "商户范围无法确认",
};
const money = new Intl.NumberFormat("zh-CN", { style: "currency", currency: "CNY" });
const date = new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", dateStyle: "short", timeStyle: "short" });
const diagnosticTime = (value?: string) => value && Number.isFinite(Date.parse(value)) ? date.format(new Date(value)) : "未记录";
function DiagnosticError({ error }: { error?: WalletErrorEvidence }) {
  if (!error) return <span>未记录</span>;
  return <span>{errorNames[error.reason] ?? "未知原因"}{Number.isSafeInteger(error.code) && <> · 代码 {error.code}</>}{Number.isSafeInteger(error.httpStatus) && <> · HTTP {error.httpStatus}</>} · {diagnosticTime(error.at)}</span>;
}
function WalletDiagnostics({ order }: { order: AdminWalletTransfer }) {
  const evidence = order.diagnostics, dispatch = evidence?.dispatch, reconciliation = evidence?.reconciliation;
  const manual = evidence?.manualResolution;
  return <details className="wallet-diagnostics"><summary>诊断信息</summary><div>
    {!dispatch && <p>历史订单未保存首次发起记录，无法据此证明已发送；缺记录也不代表未发送。</p>}
    <dl>
      {dispatch && <><div><dt>首次发起状态</dt><dd>{dispatchNames[dispatch.phase] ?? "首次请求结果未知"}</dd></div><div><dt>开始 / 结束</dt><dd>{diagnosticTime(dispatch.startedAt)} / {diagnosticTime(dispatch.finishedAt)}</dd></div><div><dt>网络请求已开始</dt><dd>{dispatch.requestStarted === true ? "是" : dispatch.requestStarted === false ? "否" : "未记录"}</dd></div><div><dt>首次响应金额</dt><dd>{Number.isSafeInteger(dispatch.acknowledgedAmountCents) ? money.format(dispatch.acknowledgedAmountCents! / 100) : "未记录"}</dd></div><div><dt>首次安全错误</dt><dd><DiagnosticError error={dispatch.error}/>{dispatch.error && <small> · 结果{dispatch.error.outcomeUnknown === true ? "未知" : dispatch.error.outcomeUnknown === false ? "明确" : "未标记"}</small>}</dd></div></>}
      <div><dt>上游状态 / 场馆代码</dt><dd>{evidence?.upstreamStatus ?? "未记录"} / {evidence?.upstreamApiCode ?? "未记录"}</dd></div>
      <div><dt>上游实际金额</dt><dd>{Number.isSafeInteger(evidence?.upstreamAmountCents) ? money.format(evidence!.upstreamAmountCents! / 100) : "未记录"}</dd></div>
      <div><dt>原单核对次数</dt><dd>{Number.isSafeInteger(reconciliation?.attempts) ? reconciliation!.attempts : "未记录"}</dd></div>
      <div><dt>上次 / 下次查单</dt><dd>{diagnosticTime(reconciliation?.lastAttemptAt)} / {diagnosticTime(reconciliation?.nextAttemptAt)}</dd></div>
      <div><dt>最近安全错误</dt><dd><DiagnosticError error={reconciliation?.lastError}/></dd></div>
      {manual && <>
        <div><dt>人工结案结果</dt><dd>{manual.decision === "succeeded" ? "确认成功" : "确认失败"}</dd></div>
        <div><dt>操作管理员 / 时间</dt><dd>{manual.reviewerId} / {diagnosticTime(manual.reviewedAt)}</dd></div>
        <div><dt>核查依据</dt><dd>{manual.note}</dd></div>
        <div><dt>原隔离原因</dt><dd>{resolutionReasonNames[manual.originalResolution.reason] ?? "需人工核查"}</dd></div>
        <div><dt>原隔离实际金额</dt><dd>{Number.isSafeInteger(manual.originalResolution.actualAmountCents) ? money.format(manual.originalResolution.actualAmountCents! / 100) : "未记录"}</dd></div>
      </>}
    </dl><p>“网络请求已开始”仅表示本地开始调用，不证明供应商已收到。是否成功以原单终态为准；诊断不代表批准、补发或退款。</p>
  </div></details>;
}
function SettlementDetails({ order }: { order: AdminWalletTransfer }) {
  const settlement = walletSettlementSummary(order);
  if (!settlement) return null;
  return <dl className="admin-wallet__settlement" aria-label="部分到账明细"><div><dt>申请</dt><dd>{money.format(settlement.requestedAmountCents / 100)}</dd></div><div><dt>{settlement.actualLabel}</dt><dd>{money.format(settlement.actualAmountCents / 100)}</dd></div><div><dt>{settlement.differenceLabel}</dt><dd>{money.format(settlement.differenceAmountCents / 100)}</dd></div></dl>;
}

type ManualDecision = "succeeded" | "failed";

function ManualResolution({ order, disabled, onResolved }: { order: AdminWalletTransfer; disabled: boolean; onResolved: () => Promise<void> }) {
  const noteId = useId();
  const amountId = useId();
  const confirmationId = useId();
  const [note, setNote] = useState("");
  const [actualAmount, setActualAmount] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [noteError, setNoteError] = useState("");
  const [amountError, setAmountError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [decision, setDecision] = useState<ManualDecision | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const parsedAmount = amountCents(actualAmount);
  const previewAmount = parsedAmount !== null && parsedAmount > 0 && parsedAmount <= order.amountCents ? parsedAmount : null;
  const confirmationMatches = confirmation === order.id;

  const prepare = (nextDecision: ManualDecision) => {
    if (disabled || submittingRef.current || !confirmationMatches) return;
    const normalizedNote = note.trim();
    const nextNoteError = normalizedNote.length < 8 ? "请填写至少 8 个字的核查依据。" : normalizedNote.length > 300 ? "核查依据最多 300 个字。" : "";
    const nextAmountError = nextDecision === "succeeded"
      ? parsedAmount === null || parsedAmount <= 0
        ? "请输入大于 0 且最多两位小数的实际金额。"
        : parsedAmount > order.amountCents
          ? `实际金额不能超过申请金额 ${money.format(order.amountCents / 100)}。`
          : ""
      : "";
    setNoteError(nextNoteError);
    setAmountError(nextAmountError);
    setSubmitError("");
    if (nextNoteError) {
      document.getElementById(noteId)?.focus();
      return;
    }
    if (nextAmountError) {
      document.getElementById(amountId)?.focus();
      return;
    }
    setDecision(nextDecision);
  };

  const submit = async () => {
    if (!decision || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError("");
    try {
      await api(`/api/admin/wallet/transfers/${encodeURIComponent(order.id)}/resolve`, {
        method: "POST",
        body: JSON.stringify({
          confirmation,
          decision,
          ...(decision === "succeeded" ? { actualAmount: actualAmount.trim() } : {}),
          note: note.trim(),
        }),
      });
      setDecision(null);
      await onResolved();
    } catch (reason) {
      setDecision(null);
      setSubmitError(reason instanceof ApiError ? reason.message : "人工结案结果未确认，请先刷新订单核对后再操作。");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return <>
    <details className="wallet-resolution">
      <summary>人工结案</summary>
      <form noValidate onSubmit={event => event.preventDefault()}>
        <p className="wallet-resolution__intro">只根据已经核实的供应商凭据结案。结案会停止该订单后续查询，并按所选结果更新资金。</p>
        {submitError && <div className="form-alert" role="alert">{submitError}</div>}
        <label className="field" htmlFor={noteId}>
          <span className="field__label" id={`${noteId}-label`}>核查依据</span>
          <textarea id={noteId} className="input textarea resize-none" rows={4} maxLength={300} value={note} aria-labelledby={`${noteId}-label`} aria-invalid={Boolean(noteError)} aria-describedby={`${noteId}-message`} disabled={submitting || disabled} onChange={event => { setNote(event.target.value); if (noteError) setNoteError(""); }} />
          <span id={`${noteId}-message`} className={`field__message ${noteError ? "field__message--error" : ""}`}>{noteError || `${note.trim().length}/300 · 填写订单号、供应商回执或核对时间，至少 8 个字。`}</span>
        </label>
        <label className="field" htmlFor={amountId}>
          <span className="field__label" id={`${amountId}-label`}>成功实际金额（元）</span>
          <input id={amountId} className="input" type="text" inputMode="decimal" placeholder={(order.amountCents / 100).toFixed(2)} value={actualAmount} aria-labelledby={`${amountId}-label`} aria-invalid={Boolean(amountError)} aria-describedby={`${amountId}-message`} disabled={submitting || disabled} onChange={event => { setActualAmount(event.target.value); if (amountError) setAmountError(""); }} />
          <span id={`${amountId}-message`} className={`field__message ${amountError ? "field__message--error" : ""}`}>{amountError || "仅按成功结案时必填，最多两位小数。"}</span>
        </label>
        <dl className="wallet-resolution__amounts" aria-label="人工结案金额预览">
          <div><dt>申请</dt><dd>{money.format(order.amountCents / 100)}</dd></div>
          <div><dt>实际</dt><dd>{previewAmount === null ? "待填写" : money.format(previewAmount / 100)}</dd></div>
          <div><dt>差额</dt><dd>{previewAmount === null ? "待填写" : money.format((order.amountCents - previewAmount) / 100)}</dd></div>
        </dl>
        <label className="field" htmlFor={confirmationId}>
          <span className="field__label" id={`${confirmationId}-label`}>二次确认订单号</span>
          <input id={confirmationId} className="input wallet-resolution__confirmation" type="text" autoComplete="off" spellCheck={false} placeholder={order.id} value={confirmation} aria-labelledby={`${confirmationId}-label`} aria-invalid={Boolean(confirmation && !confirmationMatches)} aria-describedby={`${confirmationId}-message`} disabled={submitting || disabled} onChange={event => setConfirmation(event.target.value)} />
          <span id={`${confirmationId}-message`} className={`field__message ${confirmation && !confirmationMatches ? "field__message--error" : ""}`}>{confirmationMatches ? "订单号一致，可以选择结案结果。" : "请完整输入或粘贴上方订单号；完全一致后才可结案。"}</span>
        </label>
        <div className="wallet-resolution__actions">
          <Button type="button" intent="danger" emphasis="outline" disabled={disabled || submitting || !confirmationMatches} onClick={() => prepare("failed")}>按失败结案</Button>
          <Button type="button" intent="success" disabled={disabled || submitting || !confirmationMatches} onClick={() => prepare("succeeded")}>按成功结案</Button>
        </div>
      </form>
    </details>
    <Modal open={decision !== null} title={decision === "succeeded" ? "确认按成功结案" : "确认按失败结案"} description={`订单 ${order.id}`} onClose={() => setDecision(null)} preventClose={submitting}>
      <div className="wallet-resolution__confirm">
        <p>{decision === "succeeded" ? "系统将按已核实的实际金额结算资金并关闭该订单。" : "系统将按失败规则关闭该订单，之后不再自动查询原单。"}</p>
        <dl className="wallet-resolution__amounts">
          <div><dt>申请金额</dt><dd>{money.format(order.amountCents / 100)}</dd></div>
          {decision === "succeeded" && <><div><dt>实际金额</dt><dd>{previewAmount === null ? "—" : money.format(previewAmount / 100)}</dd></div><div><dt>差额</dt><dd>{previewAmount === null ? "—" : money.format((order.amountCents - previewAmount) / 100)}</dd></div></>}
        </dl>
        <p className="wallet-resolution__note"><strong>核查依据：</strong>{note.trim()}</p>
        {submitError && <div className="form-alert" role="alert">{submitError}</div>}
        <div className="modal-actions">
          <Button type="button" emphasis="outline" disabled={submitting} onClick={() => setDecision(null)}>返回修改</Button>
          <Button type="button" intent={decision === "succeeded" ? "success" : "danger"} busy={submitting} onClick={() => void submit()}>{decision === "succeeded" ? "确认成功结案" : "确认失败结案"}</Button>
        </div>
      </div>
    </Modal>
  </>;
}

export function AdminWallet() {
  const [orders, setOrders] = useState<AdminWalletTransfer[] | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [visible, setVisible] = useState(30);
  const alive = useRef(false);
  const inFlight = useRef(false);
  const refresh = useCallback(async (id?: string) => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(id ?? "load"); setError("");
    try {
      if (id) await api(`/api/admin/wallet/transfers/${encodeURIComponent(id)}/reconcile`, { method: "POST" });
      const result = await api<{ transfers: AdminWalletTransfer[] }>("/api/admin/wallet/transfers");
      if (alive.current) setOrders(result.transfers);
    } catch (reason) { if (alive.current) setError(reason instanceof ApiError ? reason.message : "转账记录读取失败，请重试。"); }
    finally { inFlight.current = false; if (alive.current) setBusy(""); }
  }, []);
  useEffect(() => { alive.current = true; void refresh(); return () => { alive.current = false; }; }, [refresh]);
  return <section className="admin-panel admin-panel--table admin-wallet">
    <div className="panel-heading"><div><h3>场馆转账订单</h3><p>系统自动查询原单并结算，无需管理员批准。部分执行按上游实际金额结算；危险终态会停止自动查询并标记人工核查。同一场馆结案前保持锁定，其他场馆不受影响。时间为北京时间。</p></div><Button busy={busy === "load"} disabled={!!busy} onClick={() => void refresh()}>刷新订单</Button></div>
    {error && <div className="form-alert" role="alert">{error}</div>}
    {!orders ? busy ? <Spinner label="正在读取转账记录" /> : <EmptyState title="转账记录加载失败" message="请重试。" action={<Button onClick={() => void refresh()}>重试</Button>} /> : !orders.length ? <EmptyState title="暂无场馆转账" message="会员确认场馆与金额后，系统自动确认并记录结果，无需后台批准。" /> : <>
      <div className="table-scroll"><table><caption className="sr-only">持久化场馆转账订单</caption><thead><tr><th scope="col">订单 / 会员</th><th scope="col">场馆 / 方向</th><th scope="col">金额</th><th scope="col">状态</th><th scope="col">时间</th><th scope="col">操作</th></tr></thead><tbody>{orders.slice(0, visible).map(order => <tr key={order.id}>
        <td><code>{order.id}</code><p>{order.userId}</p><WalletDiagnostics order={order}/></td><td>{order.provider}<p>{order.direction === "deposit" ? "平台 → 场馆" : "场馆 → 平台"}</p></td><td>{money.format(order.amountCents / 100)}<SettlementDetails order={order}/></td><td><StatusBadge tone={order.status === "succeeded" ? "success" : order.status === "failed" ? "danger" : needsWalletReview(order) ? "warning" : "neutral"}>{walletTransferLabel(order)}</StatusBadge><p>{order.message}</p></td><td>{date.format(new Date(order.createdAt))}</td><td>{queryableTransfer(order) ? <Button emphasis="outline" busy={busy === order.id} disabled={!!busy} onClick={() => void refresh(order.id)}>查询原单</Button> : isDurableWalletReview(order) ? <ManualResolution order={order} disabled={Boolean(busy)} onResolved={() => refresh()} /> : "已结算"}</td>
      </tr>)}</tbody></table></div>
      <div className="panel-heading"><span>显示 {Math.min(visible, orders.length)} / {orders.length} 笔</span>{visible < orders.length && <Button emphasis="outline" onClick={() => setVisible(count => count + 30)}>加载更多</Button>}</div>
    </>}
  </section>;
}
